import { Session } from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import * as Sentry from '@sentry/nextjs';
import { NextPage } from 'next';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect } from 'react';
import Cookies from 'universal-cookie';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { checkQueryParam } from '@src/utils/check-query-param';
import '@aspendental/shared-components-web/lib/fonts.css';
import '@contentful/live-preview/style.css';
import { PortalLayout } from '@src/components/templates/PortalLayout/PortalLayout';
import { isSentryEnabled, toggleSentry } from '@src/services/sentry';
import { initializeSentryClient } from '@src/utils/sentryClientInit';
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  const config = pageProps?.config as BrandThemeConfig;
  const {
    features
  } = config as BrandThemeConfig;
  const brandName = config?.name || 'Aspen Dental';
  const environment = pageProps?.env?.ENVIRONMENT_NAME;
  const router = useRouter();
  toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
  useEffect(() => {
    if (isSentryEnabled()) {
      if (!Sentry.isInitialized()) {
        initializeSentryClient(environment);
      }
      Sentry.setTag('brand', brandName);
      Sentry.setContext('user', {
        sessionId: getSessionId(),
        brand: brandName
      });
      globalThis.LogRocket?.getSessionURL(sessionURL => {
        Sentry.setContext('logRocket', {
          sessionURL
        });
      });
    }
  }, [brandName]);
  useEffect(() => {
    // when component is mounting we remove server side rendered css:
    const jssStyles = document.querySelector('#jss-server-side');

    // Store the rwg_token in a cookie
    const cookies = new Cookies();

    //google reserve cookie
    const {
      rwg_token
    } = router.query;
    //get original referrer facility code
    const {
      fc
    } = router.query;
    let cookieString = '';
    if (rwg_token) {
      if (checkQueryParam('rwg_token')) {
        cookieString += rwg_token;
        cookies.set('_rwg_token', rwg_token);
      }
    }
    if (fc) {
      if (checkQueryParam('fc')) {
        cookieString += ';_merchant_id=' + fc;
        cookies.set('facility-code', fc);
      }
    }
    if (cookieString.length > 0) {
      cookieString += ';max-age=2592000;domain=aspendental.com;path=/schedule-an-appointment';
    }
    if (jssStyles?.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
    router.events.on('routeChangeComplete', () => null);
    return () => {
      router.events.off('routeChangeComplete', () => null);
    };
  }, [router.events]);
  if (router.pathname === '_error') return <Component {...pageProps} />;
  return <Session data-sentry-element="Session" data-sentry-component="App" data-sentry-source-file="_app.page.tsx">
			<ContextProviders config={pageProps.config} preview={pageProps.preview} environment={pageProps.env?.ENVIRONMENT_NAME} graphURL={pageProps.graphURL} googleURL={pageProps.googleURL} user={pageProps.user} resultURL={pageProps.resultURL} googleMapApiKey={pageProps.googleMapApiKey} eapiBaseUrl={pageProps.eapiBaseUrl} previsitPaperworkUrl={pageProps.previsitPaperworkUrl} userEventsURL={pageProps.userEventsURL} formEventsURL={pageProps.formEventsURL} appData={pageProps.appData} stateCodeFromHeaders={pageProps.stateCodeFromHeaders} data-sentry-element="ContextProviders" data-sentry-source-file="_app.page.tsx">
				<PortalLayout locale={router.locale ?? 'en-US'} preview={pageProps.preview} appData={pageProps.appData} isFooterLite={features.footer.lite} withSimpleLayout={features.footer.withSimpleLayout} footerData={pageProps.footerData} logoHref={pageProps.env?.ENVIRONMENT_NAME === 'prod' ? pageProps.config.features.livePreview.prod : pageProps?.config?.features?.livePreview.stage} pageReferrer={pageProps.pageReferrer} config={pageProps.config} data-sentry-element="PortalLayout" data-sentry-source-file="_app.page.tsx">
					<Component {...pageProps} data-sentry-element="Component" data-sentry-source-file="_app.page.tsx" />
				</PortalLayout>
			</ContextProviders>
		</Session>;
};
export default appWithTranslation(App);